import {cn} from "@/shared/lib/utils";
import {flexRender, Header} from "@tanstack/react-table";
import {TableHead} from "@/shared/components/ui/table";
import {useDrag, useDrop} from "react-dnd";
import React, {useRef} from "react";

interface AutoTableHeaderProps<TData> {
    header: Header<TData, unknown>;
    index: number;
    moveColumn: (from: number, to: number) => void;
}

interface DragItem {
    index: number;
}

export function ReactTableHeader<TData>({
                                            header,
                                            index,
                                            moveColumn,
                                        }: AutoTableHeaderProps<TData>) {
    const ref = useRef<HTMLTableCellElement | null>(null);
    const shouldBlockDragRef = useRef(false);
    const RESIZE_AREA_WIDTH = 8;

    const resizeHandler = header.getResizeHandler();
    const column = header.column;
    const isResizing = column.getIsResizing();

    const [{isDragging}, dragRef] = useDrag<DragItem, void, { isDragging: boolean }>({
        type: "column",
        item: () => ({index}),
        canDrag: (monitor) => {
            // Nie pozwól na drag jeśli:
            // 1. Jest w trakcie resize
            // 2. Kliknięcie było w obszarze resize
            if (isResizing) return false;
            if (shouldBlockDragRef.current) return false;

            const sourceClientOffset = monitor.getClientOffset();
            if (sourceClientOffset && ref.current) {
                const rect = ref.current.getBoundingClientRect();
                // Sprawdź czy kliknięcie było w obszarze resize
                const isNearRightResizeZone = sourceClientOffset.x >= rect.right - RESIZE_AREA_WIDTH;
                const isNearLeftResizeZone = sourceClientOffset.x <= rect.left + RESIZE_AREA_WIDTH;
                if (isNearRightResizeZone || isNearLeftResizeZone) {
                    return false;
                }
            }

            return true;
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{isOverCurrent}, dropRef] = useDrop<DragItem, void, { isOverCurrent: boolean }>({
        accept: "column",
        hover: (dragged, monitor) => {
            if (!dragged || isResizing) return;

            const dragIndex = dragged.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            if (!hoverBoundingRect) return;

            const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;

            const hoverClientX = clientOffset.x - hoverBoundingRect.left;
            if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) return;
            if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) return;

            moveColumn(dragIndex, hoverIndex);
            dragged.index = hoverIndex;
        },
        drop: (dragged) => {
            if (!dragged || isResizing) return;
            if (dragged.index === index) return;
            moveColumn(dragged.index, index);
            dragged.index = index;
        },
        collect: (monitor) => ({
            isOverCurrent: monitor.isOver({shallow: true}),
        }),
    });

    const attachRefs = (node: HTMLTableCellElement | null) => {
        ref.current = node;
        if (!node) return;

        dropRef(node);
        dragRef(node);
    };

    return (
        <TableHead
            ref={attachRefs}
            onMouseDown={(e) => {
                if (ref.current) {
                    const rect = ref.current.getBoundingClientRect();
                    const isInRightResizeArea = e.clientX >= rect.right - RESIZE_AREA_WIDTH;
                    const isInLeftResizeArea = e.clientX <= rect.left + RESIZE_AREA_WIDTH;
                    const isInResizeArea = isInRightResizeArea || isInLeftResizeArea;
                    shouldBlockDragRef.current = isInResizeArea;
                    if (isInResizeArea) {
                        e.stopPropagation();
                    }
                }
            }}
            onMouseUp={() => {
                shouldBlockDragRef.current = false;
            }}
            onMouseLeave={() => {
                shouldBlockDragRef.current = false;
            }}
            key={header.column.id}
            colSpan={header.colSpan}
            className={cn(
                "relative flex h-10 items-center select-none bg-neutral-50 hover:bg-neutral-200 transition-colors",
                isDragging && "opacity-55",
                isOverCurrent && !isDragging && "ring-1 ring-primary/50 bg-primary/5"
            )}
            style={{
                width: column.getSize(),
                flex: `0 0 ${column.getSize()}px`,
                boxSizing: "border-box",
                cursor: isDragging ? "grabbing" : "default",
            }}
            data-columnid={column.id}
        >
            <div className="flex h-full w-full items-center justify-start overflow-hidden px-2 pr-[7px]">
                <span className="block w-full truncate text-left text-sm font-semibold text-gray-700 leading-none">
                    {flexRender(column.columnDef.header, header.getContext())}
                </span>
            </div>
            <div
                onDoubleClick={(e) => {
                    e.stopPropagation();
                    column.resetSize();
                }}
                onMouseDown={(e) => {
                    e.stopPropagation();
                    shouldBlockDragRef.current = true;
                    resizeHandler(e.nativeEvent);
                }}
                onTouchStart={(e) => {
                    e.stopPropagation();
                    shouldBlockDragRef.current = true;
                    resizeHandler(e.nativeEvent);
                }}
                className={`absolute right-0 top-0 h-full w-[8px] hover:bg-primary/20 transition-colors duration-200 z-10 ${
                    isResizing ? "bg-secondary" : ""
                }`}
                style={{cursor: "col-resize", touchAction: "none"}}
            />
        </TableHead>
    );
}

export default ReactTableHeader;
